import { ACCESS_TOKEN_NAME_MAX } from '@hugo/core'
import { useQueryClient } from '@tanstack/react-query'
import { CopyIcon, KeyRoundIcon, TerminalIcon } from 'lucide-react'
import { useState } from 'react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from '@/components/ui/toast'
import { copyText } from '@/lib/clipboard'
import { relativeTime } from '@/lib/format'
import { queryKeys, useAccessTokens, useRunTool } from '@/lib/items'
import { mcpAddCommand } from '@/lib/mcp'
import { apiOrigin } from '@/lib/platform'
import { useUserNow } from '@/lib/session'

const TOKENS_KEY = queryKeys.accessTokens
const DEFAULT_NAME = 'Claude Code'

/**
 * Settings → Claude Code: tokens that let Claude Code (or any MCP client) read and change the
 * user's items through hugo's tools at /api/mcp. Approving what hugo sends stays in hugo. A new
 * token's secret is shown once, inside the command that adds hugo to Claude Code.
 */
export function McpCard() {
  const queryClient = useQueryClient()
  const { run } = useRunTool()
  const now = useUserNow(60_000)
  const tokens = useAccessTokens()
  const [name, setName] = useState(DEFAULT_NAME)
  const [command, setCommand] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [revoking, setRevoking] = useState<{ id: string; name: string } | null>(null)
  const origin = apiOrigin || window.location.origin

  const create = async () => {
    setBusy(true)
    const result = await run(
      'access_tokens.create',
      { name: name.trim() || DEFAULT_NAME },
      { done: 'Token created', failed: 'Could not create the token' },
    )
    if (result) setCommand(mcpAddCommand(origin, result.secret))
    await queryClient.invalidateQueries({ queryKey: TOKENS_KEY })
    setBusy(false)
  }

  const revoke = async (id: string) => {
    await run(
      'access_tokens.revoke',
      { id },
      { done: 'Token revoked', failed: 'Could not revoke the token' },
    )
    await queryClient.invalidateQueries({ queryKey: TOKENS_KEY })
  }

  const copy = async () => {
    if (!command) return
    const ok = await copyText(command)
    toast.add(
      ok
        ? { title: 'Command copied', type: 'success', timeout: 2500 }
        : { title: 'Copy did not work', description: 'Select the command and copy it yourself.' },
    )
  }

  const list = tokens.data ?? []
  return (
    <Card data-testid="mcp-settings">
      <CardHeader>
        <CardTitle>Claude Code</CardTitle>
        <CardDescription>
          Let Claude Code read and change your items through hugo's tools (MCP). It can capture,
          file, plan and draft replies; anything that would be sent still waits in your Outbox for
          you to approve here.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            void create()
          }}
        >
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <Label htmlFor="mcp-token-name">Token name</Label>
            <Input
              id="mcp-token-name"
              className="h-8"
              maxLength={ACCESS_TOKEN_NAME_MAX}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <Button type="submit" size="sm" disabled={busy}>
            <KeyRoundIcon aria-hidden="true" />
            Create token
          </Button>
        </form>

        {command ? (
          <div className="flex flex-col gap-2 rounded-lg border p-3" data-testid="mcp-command">
            <p className="flex items-center gap-1.5 text-sm font-medium">
              <TerminalIcon aria-hidden="true" className="size-4" />
              Run this in a terminal to add hugo to Claude Code
            </p>
            <code className="block overflow-x-auto rounded bg-muted px-2 py-1.5 font-mono text-xs break-all whitespace-pre-wrap select-all">
              {command}
            </code>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs text-muted-foreground">
                The token is shown only now. Anyone with it can act as you in hugo.
              </p>
              <Button size="sm" variant="outline" onClick={() => void copy()}>
                <CopyIcon aria-hidden="true" />
                Copy
              </Button>
            </div>
          </div>
        ) : null}

        {list.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="no-tokens">
            No tokens yet.
          </p>
        ) : (
          <ul className="divide-y rounded-lg border">
            {list.map((token) => (
              <li
                key={token.id}
                className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
                data-testid="token-row"
              >
                <span className="flex min-w-0 flex-col">
                  <span className="truncate text-sm font-medium">{token.name}</span>
                  <span className="text-xs text-muted-foreground">
                    <span className="font-mono">{token.prefix}…</span> · created{' '}
                    {relativeTime(new Date(token.createdAt), now)} ·{' '}
                    {token.lastUsedAt
                      ? `used ${relativeTime(new Date(token.lastUsedAt), now)}`
                      : 'not used yet'}
                  </span>
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setRevoking({ id: token.id, name: token.name })}
                >
                  Revoke
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      <AlertDialog open={revoking !== null} onOpenChange={(open) => !open && setRevoking(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke “{revoking?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              Claude Code (or whatever uses this token) can no longer reach hugo. This cannot be
              undone; create a new token to connect again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (revoking) void revoke(revoking.id)
                setRevoking(null)
              }}
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}
